import { pick } from 'lodash-es';

import type { VBaseTableProps, VBaseTableRenderCell } from '@/package';
import {
  type VBaseTableRenderColumn,
  VInput,
  VStack,
  VTable,
  type VTableProps,
  new__cn as cn,
} from '@/package';
import type { User } from '@/type';

export type MemberTaskBaseRow<T extends Dictionary> = {
  taskRatio: number;
} & User &
  T;

type MemberTaskBaseTableProps<
  T extends Dictionary,
  D extends {
    id: User['id'];
    member: string;
    taskRatio: number;
  } & { [K in keyof T]: T[K] },
> = {
  data: MemberTaskBaseRow<T>[];
  middleColumn?: {
    column: keyof T;
    renderColumn?: <K extends keyof T>(column: K) => React.ReactNode;
    renderCell?: <K extends keyof T>(
      cellValue: T[K],
      column: K,
      cell: T,
    ) => React.ReactNode;
  }[];
  removable?: boolean;
  onTaskRatioChange?: (userID: User['id'], taskRatio: number) => void;
} & Pick<VTableProps<D>, 'onRemove'>;

export const MemberTaskBaseTable = <
  T extends Dictionary,
  D extends {
    id: User['id'];
    member: string;
    taskRatio: number;
  } & { [K in keyof T]: T[K] },
>({
  data,
  middleColumn = [],
  removable,
  onRemove,
  onTaskRatioChange,
}: MemberTaskBaseTableProps<T, D>) => {
  const middleColumnNameList = middleColumn.map(c => c.column);
  const itemList = data.map(
    ({ email, id, name, taskRatio, ...row }) =>
      ({
        id,
        member: name ? name : email,
        taskRatio,
        ...pick(row, middleColumnNameList),
      }) as unknown as D,
  );
  const columnList = [
    'member',
    ...middleColumnNameList,
    'taskRatio',
  ] as (keyof D)[];

  return (
    <VStack className="h-full">
      <VTable<D>
        cellRenderer={
          {
            member: value => {
              return <div className="text-body-md">{value}</div>;
            },
            taskRatio: (value, _, row) => {
              return (
                <VInput
                  value={value}
                  min={0}
                  max={100}
                  renderRightSide={() => '%'}
                  size="sm"
                  step={1}
                  type="number"
                  onChange={e => {
                    onTaskRatioChange?.(row.id, Number(e.target.value));
                  }}
                />
              );
            },
            ...middleColumn.reduce(
              (acc, cur) => ({
                ...acc,
                [cur.column]: (
                  ...args: Parameters<
                    VBaseTableRenderCell<MemberTaskBaseRow<T>, keyof T>
                  >
                ) => (
                  <div className="text-body-md">
                    {cur.renderCell?.(...args)}
                  </div>
                ),
              }),
              {} as {
                [K in keyof T]: VBaseTableRenderCell<MemberTaskBaseRow<T>, K>;
              },
            ),
          } as VBaseTableProps<D>['cellRenderer']
        }
        className="overflow-h-auto h-[389px] overflow-x-hidden"
        columnList={columnList as VBaseTableProps<D>['columnList']}
        columnRenderer={
          middleColumn.reduce(
            (acc, cur) => ({ ...acc, [cur.column]: cur.renderColumn }),
            {} as {
              [K in keyof T]: VBaseTableRenderColumn<MemberTaskBaseRow<T>, K>;
            },
          ) as VBaseTableProps<D>['columnRenderer']
        }
        columnStyleMap={{
          member: {
            className: cn('flex w-[158px] items-center text-body-sm'),
          },
          taskRatio: {
            className: cn('flex w-[100px] items-center text-body-sm'),
          },
          ...middleColumn.reduce(
            (acc, cur) => ({
              ...acc,
              [cur.column]: { className: cn('flex items-center text-body-sm') },
            }),
            {} as {
              [K in keyof T]: { className: string };
            },
          ),
          remove: {
            className: cn('flex items-center text-body-sm', {
              'hidden w-0': !removable,
              'w-[54px] ': removable,
            }),
          },
        }}
        data={itemList}
        rowSize="md"
        setRowRemovable={() => !!removable}
        onRemove={id => {
          onRemove?.(id);
        }}
      />
    </VStack>
  );
};
